"use client";

import React, { useEffect, useState } from "react";
import { MapPin, Sparkles } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
type SectionLanguage = "en" | "it";
const luxuryHotels = [{
  id: "lux-1",
  name: "Hotel Rome Cavalieri",
  image: "https://saraandmichael2026.com/wp-content/uploads/2026/04/Party-in-Planetarium-Suite-Rooftop-Terrace-e1469603819575.jpg?v=0sle7gj5rylr",
  priceRange: "",
  neighborhood: {
    en: "Balduina",
    it: "Balduina"
  },
  proTip: {
    en: "Location of the Reception",
    it: "Location della Reception"
  }
}, {
  id: "lux-2",
  name: "Navona Queen Rooftop, Rome",
  image: "https://saraandmichael2026.com/wp-content/uploads/2026/04/Navona-Queen-Rooftop.jpg?v=jlysnyva0kq",
  priceRange: "",
  neighborhood: {
    en: "Piazza Navona",
    it: "Piazza Navona"
  },
  proTip: {
    en: "6 min drive (11 min walk) to the Ceremony. 20 min drive to the Reception",
    it: "6 minuti in macchina (11 minuti a piedi) dalla Chiesa.  20 minuti in macchina dalla Reception"
  }
}, {
  id: "lux-3",
  name: "Donna Camilla Savelli",
  image: "https://saraandmichael2026.com/wp-content/uploads/2026/04/Donna-Camilla-Savelli.jpg?v=7j22e42b8tj",
  priceRange: "",
  neighborhood: {
    en: "Trastevere",
    it: "Trastevere"
  },
  proTip: {
    en: "15 min drive to the Ceremony. 20 min drive to the Reception",
    it: "15 minuti in macchina dalla Chiesa.  20 minuti in macchina dalla Reception"
  }
}];
const boutiqueHotels = [{
  id: "bou-1",
  name: "Airbnb",
  image: "https://saraandmichael2026.com/wp-content/uploads/2026/04/airbnb.png",
  link: "https://www.airbnb.com/wishlists/v/2014046132?view_only_token=25552269-3ef4-4b4e-a4bf-8f82ffb32e58",
  priceRange: {
    en: "Flexible",
    it: "Flessibile"
  },
  neighborhood: {
    en: "Rome",
    it: "Roma"
  },
  proTip: {
    en: "Airbnb is a great option in Rome, with many apartments throughout the city. Staying near a metro stop or in a central neighborhood can make getting around much easier. Be sure to check check-in times and luggage storage, as they can vary by host. The neighborhood Prati is likely the most convenient to both get to the wedding easily and explore the city.",
    it: "Airbnb è un’ottima opzione a Roma, con molti appartamenti disponibili in tutta la città. Soggiornare vicino a una fermata della metropolitana o in un quartiere centrale può rendere gli spostamenti molto più semplici. Vi consigliamo di verificare con attenzione orari di check-in e deposito bagagli, che possono variare da host a host. Il quartiere Prati è probabilmente uno dei più comodi sia per raggiungere facilmente il matrimonio sia per esplorare la città."
  }
}];
export default function RomanSojourn() {
  const [language, setLanguage] = useState<SectionLanguage>("en");
  useEffect(() => {
    const savedLanguage = localStorage.getItem("site-language");
    if (savedLanguage === "en" || savedLanguage === "it") {
      setLanguage(savedLanguage);
    }
  }, []);
  useEffect(() => {
    const handleStorageChange = () => {
      const savedLanguage = localStorage.getItem("site-language");
      if (savedLanguage === "en" || savedLanguage === "it") {
        setLanguage(savedLanguage);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
    };
  }, []);
  const t = {
    en: {
      travelEyebrow: "Travel",
      flightsTitle: "Flights to Rome",
      flightsIntro: "A few recommendations to help make your journey to Rome as smooth as possible.",
      recommendedAirportLabel: "Recommended Airport:",
      recommendedAirportValue: "Rome Fiumicino (FCO)",
      alternateAirport: "Rome Ciampino (CIA) is also an option, though Fiumicino is the primary international airport.",
      fromUsLabel: "From New Orleans & the U.S.",
      fromUsText: "there are no direct flights to Rome.",
      preferredRouteLead: "Our preferred route is",
      preferredRoute: "New Orleans → London → Rome",
      preferredRouteText: "which typically offers the most reliable connections.",
      connectionLead: "We recommend allowing",
      connectionTime: "at least 2–2.5 hours",
      connectionText: "for international connections, as customs and security in Europe can take time. Short layovers can be risky.",
      arrivalLead: "For a more relaxed start to the weekend, we suggest arriving in Rome",
      arrivalTime: "at least one day early",
      travelRecommendations: "Rome Recommendations",
      stayTitle: "Where to Stay",
      stayIntro: "Here are some of our recommendations for where to stay during the wedding weekend.",
      hotelsTab: "Hotels",
      airbnbTab: "Airbnb",
      proTip: "Notes",
      clickFavorites: "Click image to view our favorites"
    },
    it: {
      travelEyebrow: "Viaggio",
      flightsTitle: "Voli per Roma",
      flightsIntro: "Alcuni consigli per rendere il vostro viaggio verso Roma il più semplice possibile.",
      recommendedAirportLabel: "Aeroporto consigliato:",
      recommendedAirportValue: "Roma Fiumicino (FCO)",
      alternateAirport: "Anche l’aeroporto di Roma Ciampino (CIA) può essere un’opzione, anche se Fiumicino è il principale aeroporto internazionale.",
      fromUsLabel: "Da New Orleans e dagli Stati Uniti",
      fromUsText: "non ci sono voli diretti per Roma.",
      preferredRouteLead: "Il nostro itinerario preferito è",
      preferredRoute: "New Orleans → Londra → Roma",
      preferredRouteText: "che in genere offre le coincidenze più affidabili.",
      connectionLead: "Vi consigliamo di prevedere",
      connectionTime: "almeno 2–2,5 ore",
      connectionText: "per le coincidenze internazionali, poiché controlli di sicurezza e formalità doganali in Europa possono richiedere tempo. Le coincidenze troppo brevi possono essere rischiose.",
      arrivalLead: "Per iniziare il weekend con maggiore tranquillità, vi consigliamo di arrivare a Roma",
      arrivalTime: "almeno un giorno prima",
      travelRecommendations: "Consigli su Roma",
      stayTitle: "Dove Soggiornare",
      stayIntro: "Ecco alcune delle nostre proposte su dove soggiornare durante il weekend del matrimonio.",
      hotelsTab: "Hotel",
      airbnbTab: "Airbnb",
      proTip: "Consiglio",
      clickFavorites: "Clicca sull’immagine per vedere i nostri preferiti"
    }
  }[language];
  return <section id="roman-sojourn" className="bg-background py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-24">
          <h2 className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
            {t.travelEyebrow}
          </h2>

          <h1 className="font-serif text-5xl sm:text-6xl text-foreground leading-tight">
            {t.flightsTitle}
          </h1>

          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            {t.flightsIntro}
          </p>

          <div className="mt-10 text-left space-y-6 text-muted-foreground leading-relaxed">
            <p>
              <span className="font-semibold text-foreground">
                {t.recommendedAirportLabel}
              </span>{" "}
              {t.recommendedAirportValue}
            </p>

            <p>{t.alternateAirport}</p>

            <p>
              <span className="font-semibold text-foreground">
                {t.fromUsLabel}
              </span>{" "}
              {t.fromUsText}
            </p>

            <p>
              {t.preferredRouteLead}{" "}
              <span className="font-semibold text-foreground">
                {t.preferredRoute}
              </span>, {t.preferredRouteText}
            </p>

            <p>
              {t.connectionLead}{" "}
              <span className="font-semibold text-foreground">
                {t.connectionTime}
              </span>{" "}
              {t.connectionText}
            </p>

            <p>
              {t.arrivalLead}{" "}
              <span className="font-semibold text-foreground">
                {t.arrivalTime}
              </span>.
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-3xl text-center mb-20">
          <h2 className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
            {t.travelRecommendations}
          </h2>

          <h1 className="font-serif text-5xl sm:text-6xl text-foreground leading-tight">
            {t.stayTitle}
          </h1>

          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            {t.stayIntro}
          </p>
        </div>

        <Tabs defaultValue="luxury" className="w-full flex flex-col items-center">
          <TabsList className="mb-14 grid w-full grid-cols-2 gap-4 bg-transparent p-0 h-auto shadow-none sm:max-w-md">
            <TabsTrigger value="luxury" className="rounded-none border border-primary/30 bg-background px-6 py-4 text-[12px] tracking-[3px] uppercase text-foreground/80 transition-all duration-300 hover:-translate-y-[1px] hover:shadow-md hover:border-primary/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md">
              {t.hotelsTab}
            </TabsTrigger>

            <TabsTrigger value="boutique" className="rounded-none border border-primary/30 bg-background px-6 py-4 text-[12px] tracking-[3px] uppercase text-foreground/80 transition-all duration-300 hover:-translate-y-[1px] hover:shadow-md hover:border-primary/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md">
              {t.airbnbTab}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="luxury" className="w-full mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
              {luxuryHotels.map((hotel, i) => <Card key={hotel.id} data-index={i} className="bg-card rounded-none p-4 pb-8 shadow-md border-border/40 hover:shadow-lg transition-all duration-300 flex flex-col group">
                  <div className="aspect-[4/5] w-full overflow-hidden bg-muted mb-6 relative">
                    <img src={hotel.image} alt={hotel.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>

                  <div className="flex flex-col flex-grow px-2">
                    <div className="flex justify-between items-start mb-3 gap-4">
                      <h3 className="font-serif text-2xl text-foreground leading-tight">
                        {hotel.name}
                      </h3>
                      <Badge variant="secondary" className="font-mono tracking-widest bg-secondary/50 text-secondary-foreground">
                        {hotel.priceRange}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6">
                      <MapPin className="size-4 text-primary/70" />
                      <span>{hotel.neighborhood[language]}</span>
                    </div>

                    <div className="mt-auto bg-accent/30 p-5 border-l-2 border-primary/50 relative">
                      <Sparkles className="size-4 text-primary absolute top-4 right-4 opacity-50" />
                      <span className="block text-xs font-semibold uppercase tracking-wider text-primary mb-1.5">
                        {t.proTip}
                      </span>
                      <p className="text-[15px] text-foreground/80 italic leading-7">
                        "{hotel.proTip[language]}"
                      </p>
                    </div>
                  </div>
                </Card>)}
            </div>
          </TabsContent>

          <TabsContent value="boutique" className="w-full mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
              {boutiqueHotels.map((hotel, i) => <Card key={hotel.id} data-index={i} className="bg-card rounded-none p-4 pb-8 shadow-md border-border/40 hover:shadow-lg transition-all duration-300 flex flex-col group">
                  <div className="aspect-[4/5] w-full overflow-hidden bg-muted mb-4 relative">
                    <a href={hotel.link || "#"} target="_blank" rel="noopener noreferrer" className="block h-full w-full">
                      <img src={hotel.image} alt={hotel.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 cursor-pointer" />
                    </a>
                  </div>

                  <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-3 mb-5 text-center">
                    {t.clickFavorites}
                  </p>

                  <div className="flex flex-col flex-grow px-2">
                    <div className="flex justify-between items-start mb-3 gap-4">
                      <h3 className="font-serif text-2xl text-foreground leading-tight">
                        {hotel.name}
                      </h3>
                      <Badge variant="secondary" className="font-mono tracking-widest bg-secondary/50 text-secondary-foreground">
                        {hotel.priceRange[language]}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6">
                      <MapPin className="size-4 text-primary/70" />
                      <span>{hotel.neighborhood[language]}</span>
                    </div>

                    <div className="mt-auto bg-accent/30 p-5 border-l-2 border-primary/50 relative">
                      <Sparkles className="size-4 text-primary absolute top-4 right-4 opacity-50" />
                      <span className="block text-xs font-semibold uppercase tracking-wider text-primary mb-1.5">
                        {t.proTip}
                      </span>
                      <p className="text-[15px] text-foreground/80 italic leading-7">
                        {hotel.proTip[language]}
                      </p>
                    </div>
                  </div>
                </Card>)}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>;
}